import * as React from "react";

/**
 * Which company and site the console is currently working in.
 *
 * ── This is a *client* concern, not a request one ───────────────────────────
 *
 * The site never travels implicitly. A site-scoped request names its site in
 * the path - `/sites/12/users` - so what the server answers is written on the
 * request and readable in a log, a browser network tab or a pasted curl. This
 * module only remembers which site the reader last chose, so the pages can
 * build those paths.
 *
 * That is why it is a module with subscribers rather than a context: it is
 * persisted, it outlives any one route, and `useSyncExternalStore` gives the
 * components a subscription without another provider in `App.tsx`.
 *
 * ── One site, never "all" ───────────────────────────────────────────────────
 *
 * There is no all-sites value. Every screen is a single site, so `siteId` is a
 * real id once the session resolves, and `null` means only "the profile has not
 * landed yet" - during which no site-scoped page is mounted.
 *
 * ── Persistence ─────────────────────────────────────────────────────────────
 *
 * Keyed by user id, so two accounts on one browser do not inherit each other's
 * last site. A stored site that is no longer reachable resolves itself: the
 * switcher cannot find it in the site list and falls back to the user's home.
 */
export interface ActiveScope {
  /** The company the switcher is pointed at. Which sites are offered follows it. */
  companyId: string | null;
  /** The site every site-scoped path is built from. */
  siteId: string | null;
}

const EMPTY: ActiveScope = { companyId: null, siteId: null };

let current: ActiveScope = EMPTY;
let owner: string | null = null;

const listeners = new Set<() => void>();

const storageKey = (userId: string) => `taskiq.scope.${userId}`;

function emit(): void {
  for (const listener of listeners) listener();
}

function read(userId: string): ActiveScope | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ActiveScope>;
    if (typeof parsed.siteId !== "string") return null;

    return {
      siteId: parsed.siteId,
      companyId: typeof parsed.companyId === "string" ? parsed.companyId : null,
    };
  } catch {
    // Private browsing, a quota error, or a value from an older shape. None is
    // worth failing sign-in over; the home site is always a correct answer.
    return null;
  }
}

function write(userId: string, scope: ActiveScope): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(scope));
  } catch {
    /* see read() */
  }
}

/**
 * Point the console at a user's home, or at whatever they last had open.
 *
 * Called when a profile resolves. Idempotent per user id, so the auth listener
 * firing again on a token refresh does not throw away a site they switched to.
 */
export function initActiveScope(userId: string, homeCompanyId: string, homeSiteId: string): void {
  if (owner === userId) return;

  owner = userId;
  current = read(userId) ?? { companyId: homeCompanyId, siteId: homeSiteId };
  emit();
}

export function setActiveScope(scope: ActiveScope): void {
  current = scope;
  if (owner) write(owner, scope);
  emit();
}

/** Forget everything on sign-out, so the next account starts from its own home. */
export function clearActiveScope(): void {
  current = EMPTY;
  owner = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => current;

export function useActiveScope(): ActiveScope {
  return React.useSyncExternalStore(subscribe, snapshot, snapshot);
}
